import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { AuthProvider } from './lib/auth-context.js';
import { BrandProvider } from './lib/brand-context.js';
import { ThemeProvider } from './lib/theme-context.js';
import { SignInPage } from './pages/SignInPage.js';
import { PublicHowItWorksPage } from './pages/PublicHowItWorksPage.js';
import { Layout } from './components/Layout.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { OverviewPage } from './pages/OverviewPage.js';
import { ResponsesPage } from './pages/ResponsesPage.js';
import { SourcesPage } from './pages/SourcesPage.js';
import { PromptsPage } from './pages/PromptsPage.js';
import { PlaybooksPage } from './pages/PlaybooksPage.js';
import { PlaybookDetailPage } from './pages/PlaybookDetailPage.js';
import { ImpactPage } from './pages/ImpactPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { ResponseDetailPage } from './pages/ResponseDetailPage.js';
import { ComparisonPage } from './pages/ComparisonPage.js';
import { DomainDetailPage } from './pages/DomainDetailPage.js';
import { ActionsEarnedPage } from './pages/ActionsEarnedPage.js';
import { ActionsOwnedPage } from './pages/ActionsOwnedPage.js';
import { CollectionPage } from './pages/CollectionPage.js';
import { SchedulePage } from './pages/SchedulePage.js';
import { UrlsPage } from './pages/UrlsPage.js';
import { KeywordsPage } from './pages/KeywordsPage.js';
import { LlmsTxtPage } from './pages/LlmsTxtPage.js';
// Spec 186 — in-app "How GEO Observatory works" explainer (static, frontend-only).
import { HowItWorksPage } from './pages/HowItWorksPage.js';
// Spec 129 — /brands route retired (brand management moved into Settings).
// Spec 237 then DELETED BrandsPage.tsx: it had been unrouted and unimported
// since 129, and carrying a 400-line file nobody could reach was costing more
// than the reversibility was worth. Its sharing panel did not go with it — it
// was extracted into settings/BrandSharingSection.tsx by spec 076 and is live.
// The file itself is in the history if it is ever wanted back.
// Spec 105 — Persona Builder (brand-scoped, version-pinned personas)
import { PersonasPage } from './pages/PersonasPage.js';
// Spec 106 — Response-Level Persona-Fit (observational findings)
import { PersonaEvaluationsPage } from './pages/PersonaEvaluationsPage.js';
// Spec 107 — Page-Level Persona Evaluation (page persona-fit + three-lens panel)
import { PagePersonaEvaluationsPage } from './pages/PagePersonaEvaluationsPage.js';
import { PersonaDiagnosisDetailPage } from './pages/PersonaDiagnosisDetailPage.js';
import { PersonaDiagnosisPage } from './pages/PersonaDiagnosisPage.js';
import { AdminHomePage } from './pages/AdminHomePage.js';
import { RuleCandidatesPage } from './pages/admin/RuleCandidatesPage.js';
import { RuleCandidateDetailPage } from './pages/admin/RuleCandidateDetailPage.js';
import { RuleSuggestionsPage } from './pages/admin/RuleSuggestionsPage.js';
// Spec 103 — candidate clustering -> new-rule proposals review queue
import { RuleProposalsPage } from './pages/admin/RuleProposalsPage.js';
import { RuleSourcesPage } from './pages/admin/RuleSourcesPage.js';
// Spec 084 — rule library editor (Phase 9 Session A gap 084-1)
import { RuleLibraryPage } from './pages/admin/RuleLibraryPage.js';
import { RuleDetailPage } from './pages/admin/RuleDetailPage.js';
// Spec 088a — feature flags admin (Phase 9 Session A gap 088a-1)
import { FeatureFlagsPage } from './pages/admin/FeatureFlagsPage.js';
// Spec 087 — coverage dashboard + detail
import { CoverageDashboardPage } from './pages/coverage/CoverageDashboardPage.js';
import { CoverageAnalysisPage } from './pages/coverage/CoverageAnalysisPage.js';
// Spec 083 — page evaluations (Phase 9 Session C gap 083-1)
import { PageEvaluationsPage } from './pages/admin/PageEvaluationsPage.js';
import { PageEvaluationNewPage } from './pages/admin/PageEvaluationNewPage.js';
import { PageEvaluationDetailPage } from './pages/admin/PageEvaluationDetailPage.js';
// Spec 086 — fan-out graph (Phase 9 Session C gap 086-1)
import { FanOutGraphPage } from './pages/fanout/FanOutGraphPage.js';
import { FanOutClusterDetailPage } from './pages/fanout/FanOutClusterDetailPage.js';

/**
 * Spec 247 — two route trees.
 *
 * Signed-out routes are matched FIRST and OUTSIDE AuthProvider/BrandProvider,
 * because both providers make a protected request on mount. Inside them, a
 * signed-out visitor would 401 on the sign-in page, be redirected to the
 * sign-in page, reload, and 401 again — forever. That loop cannot reproduce
 * locally (ORBIT_DEV bypasses auth), so gate 247 forces real 401s to prove it
 * settles.
 */
export function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signin/how-it-works" element={<PublicHowItWorksPage />} />
          <Route
            path="*"
            element={
              <AuthProvider>
                <BrandProvider>
                  <AuthedRoutes />
                </BrandProvider>
              </AuthProvider>
            }
          />
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
  );
}

/** Every route that needs a signed-in person. Descendant of the `*` route above. */
function AuthedRoutes() {
  return (
            <Routes>
              <Route element={<Layout />}>
                <Route index element={<Navigate to="/overview" replace />} />
                {/* Spec 186 — static explainer; auth-protected like every app
                    route, no data fetches. */}
                <Route path="how-it-works" element={<HowItWorksPage />} />
                <Route path="overview" element={<OverviewPage />} />
                <Route path="collection" element={<CollectionPage />} />
                <Route path="schedule" element={<SchedulePage />} />
                <Route path="responses" element={<ResponsesPage />} />
                <Route path="responses/:responseId" element={<ResponseDetailPage />} />
                <Route path="responses/compare" element={<ComparisonPage />} />
                <Route path="sources" element={<SourcesPage />} />
                <Route path="sources/urls" element={<UrlsPage />} />
                <Route path="keywords" element={<KeywordsPage />} />
                <Route path="sources/:domainId" element={<DomainDetailPage />} />
                <Route path="prompts" element={<PromptsPage />} />
                <Route path="playbooks" element={<PlaybooksPage />} />
                <Route path="playbooks/:playbookId" element={<PlaybookDetailPage />} />
                <Route path="llms-txt" element={<LlmsTxtPage />} />
                <Route path="actions/earned" element={<ActionsEarnedPage />} />
                <Route path="actions/owned" element={<ActionsOwnedPage />} />
                <Route path="impact" element={<ImpactPage />} />
                {/* Spec 129 — /brands route removed (brand management moved
                    into Settings). Spec 237 deleted BrandsPage.tsx itself. */}
                {/* Spec 123 — /tags route removed (owner: no current use).
                    Spec 237 deleted TagsPage.tsx; tag management lives in
                    settings/TagManager. */}
                {/* Spec 105 — Persona Builder. Brand-scoped; the page hides
                    mutation controls for non-admins and the API is admin-gated +
                    feature-flagged behind feature.personas. */}
                <Route path="personas" element={<PersonasPage />} />
                {/* Spec 106 — Response persona-fit (observational findings).
                    Brand-scoped; controls hidden for non-admins; API admin-gated. */}
                <Route path="persona-evaluations" element={<PersonaEvaluationsPage />} />
                {/* Spec 107 — Page persona-fit (content-quality hypothesis lens).
                    Brand-scoped; controls hidden for non-admins; API admin-gated. */}
                <Route path="page-persona-evaluations" element={<PagePersonaEvaluationsPage />} />
                {/* Spec 109 — the persona-diagnosis deliverable: summary (ranked by
                    severity x confidence) + the full outcome->cause->fix detail. */}
                <Route path="persona-diagnoses" element={<PersonaDiagnosisPage />} />
                <Route
                  path="persona-diagnoses/:diagnosisId"
                  element={<PersonaDiagnosisDetailPage />}
                />
                <Route path="settings" element={<SettingsPage />} />
                {/* Spec 087 — Sitemap coverage (consultant-facing, role-unrestricted view) */}
                <Route path="coverage" element={<CoverageDashboardPage />} />
                <Route path="coverage/:analysisId" element={<CoverageAnalysisPage />} />
                {/* Spec 086 — Fan-out (consultant-facing, read-only, no role gate) */}
                <Route path="fanout" element={<FanOutGraphPage />} />
                <Route path="fanout/clusters/:slug" element={<FanOutClusterDetailPage />} />
                {/* Spec 088c — consultant-only area. Downstream specs 082/083/084/087
                    mount their admin routes under /admin/* inside this same guard. */}
                <Route
                  path="admin"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <AdminHomePage />
                    </ProtectedRoute>
                  }
                />
                {/* Spec 084 — rule library editor (Phase 9 Session A).
                    `:id` is always a UUID; literal segments under /admin/rules/*
                    (candidates, suggestions) are matched by React Router before
                    the `:id` param at the same depth regardless of declaration
                    order. */}
                <Route
                  path="admin/rules"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <RuleLibraryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/rules/:id"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <RuleDetailPage />
                    </ProtectedRoute>
                  }
                />
                {/* Spec 082 — rule-ingestion review queue (admin-only) */}
                <Route
                  path="admin/rules/candidates"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <RuleCandidatesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/rules/candidates/:id"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <RuleCandidateDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/rules/suggestions"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <RuleSuggestionsPage />
                    </ProtectedRoute>
                  }
                />
                {/* Spec 103 — rule proposals (clustered candidates) review queue */}
                <Route
                  path="admin/rules/proposals"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <RuleProposalsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/rule-sources"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <RuleSourcesPage />
                    </ProtectedRoute>
                  }
                />
                {/* Spec 088a — feature flags admin (orbit:admin only, stricter
                    than the other admin routes which also accept rule_admin). */}
                <Route
                  path="admin/feature-flags"
                  element={
                    <ProtectedRoute requireRoles={['orbit:admin']}>
                      <FeatureFlagsPage />
                    </ProtectedRoute>
                  }
                />
                {/* Spec 083 — Page Evaluations admin (Phase 9 Session C gap 083-1) */}
                <Route
                  path="admin/evaluations"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <PageEvaluationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/evaluations/new"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <PageEvaluationNewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/evaluations/:id"
                  element={
                    <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
                      <PageEvaluationDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/overview" replace />} />
              </Route>
            </Routes>
  );
}
